import { useEffect, useState, useCallback } from "react"
import { useMutation } from "@tanstack/react-query"
import { sdk } from "@/api/sdk"
import {
  formatPhoneNumberString,
  isPhoneValidLength,
} from "@/utils/StringUtils"
import { queryClient } from "@/queries/client"
import { useCreatePreApprovalRequest } from "@/queries/loan"
import { type UpdateUserProfilePhoneNumberMutation } from "@/generated/graphql"

interface PhoneNumberInputReturnType {
  phoneNumber?: string | null
  phoneNumberError: string | undefined
  onPhoneChange: (value: string | null | undefined) => void
  onSavePhoneNumber: () => void
  onPreApprovalRequest: (propertyId?: string | null | undefined) => void
  isPending: boolean
  validatePhoneNumber: () => boolean
  isValid: boolean
}

export const usePhoneNumberInput = (
  initialValue: string | null | undefined,
  onSuccess: () => void
): PhoneNumberInputReturnType => {
  const formattedInitialValue = formatPhoneNumberString(initialValue || "")
  const [phoneNumber, setPhoneNumber] = useState<string>(formattedInitialValue)
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>(
    undefined
  )
  const [isValid, setIsValid] = useState<boolean>(false)

  const { mutate: updateProfilePhoneNumber, isPending: isUpdating } =
    useMutation({
      mutationFn: async (phoneNumber: string) => {
        return await sdk.updateUserProfilePhoneNumber({ phoneNumber })
      },
      onSuccess: useCallback((data: UpdateUserProfilePhoneNumberMutation) => {
        console.log("Phone number update successful:", data)
        void queryClient.invalidateQueries({ queryKey: ["userProfile"] })
        onSuccess()
      }, []),
      onError: useCallback((error: Error) => {
        console.error("Error updating phone number:", error)
        setPhoneNumberError("Operation failed. Please try again.")
      }, []),
    })

  const { mutate: createPreApprovalRequestMutate, isPending: isCreating } =
    useCreatePreApprovalRequest()

  useEffect(() => {
    validatePhoneNumber()
  }, [phoneNumber, initialValue])

  const onPhoneChange = (value: string | null | undefined) => {
    const formattedValue = formatPhoneNumberString(value || "")
    setPhoneNumber(formattedValue)
    validatePhoneNumber()
  }

  const validatePhoneNumber = (): boolean => {
    const valid: boolean =
      (phoneNumber && isPhoneValidLength(phoneNumber)) || false
    setPhoneNumberError(valid ? undefined : "Phone number is invalid.")
    setIsValid(valid)
    return valid
  }

  const onSavePhoneNumber = () => {
    if (!phoneNumber || !isValid) return
    updateProfilePhoneNumber(phoneNumber)
  }

  const onPreApprovalRequest = (propertyId?: string | null | undefined) => {
    if (!phoneNumber || !isValid) return
    createPreApprovalRequestMutate(
      { phoneNumber, propertyId },
      {
        onSuccess: () => {
          onSuccess()
        },
        // onError can be defined here if needed
      }
    )
  }

  return {
    phoneNumber,
    phoneNumberError,
    onPhoneChange,
    onSavePhoneNumber,
    onPreApprovalRequest,
    isPending: isUpdating || isCreating,
    validatePhoneNumber,
    isValid,
  }
}
